export interface CourseSprint {
  id: number;
  title: string;
  subtitle: string;
  hours: number;
  description: string;
  icon: string;
  color: string;
}

export interface PainPointItem {
  id: string;
  groupName: string;
  problem1: string;
  problem2: string;
  problem3: string;
  chosenPain: string;
  proposedSolution: string;
  impactScore: number;
  createdAt: string;
}

export interface CanvasBlock {
  id: string;
  title: string;
  subtitle: string;
  category: 'infra' | 'core' | 'market' | 'finance';
  icon: string;
  placeholder: string;
  content: string;
}

export const COURSE_INFO = {
  title: "Empreendedorismo em Redes",
  subtitle: "Inovação Tecnológica e Novos Negócios em TIC",
  workload: "40 Horas",
  focus: "Inovação Tecnológica e Novos Negócios em TIC",
  heroQuote: {
    quote: "O código perfeito não resolve o problema errado.",
    sub: "Habilidades técnicas constroem a solução. Habilidades empreendedoras garantem que alguém queira usá-la."
  }
};

export const AULA_1_DATA = {
  title: "Dinâmica das Dores em Redes",
  subtitle: "Atividade prática em grupo para identificar e discutir problemas reais do mercado de redes.",
  duration: "60 min",
  badge: "🛠️ Atividade Prática / Dinâmica",
  steps: [
    {
      step: 1,
      icon: "groups",
      title: "Passo 1: Formação dos Grupos",
      description: "Os alunos serão divididos em grupos de 4 a 5 integrantes para realizar a atividade colaborativa."
    },
    {
      step: 2,
      icon: "assignment",
      title: "Passo 2: Listagem de Dores",
      description: "Cada grupo lista 3 problemas comuns que já enfrentaram em redes/conectividade. Exemplos: Wi-Fi caindo, falta de segurança, suporte lento."
    },
    {
      step: 3,
      icon: "ads_click",
      title: "Passo 3: Escolha da Dor",
      description: "Cada grupo escolhe uma dor principal para discutir em profundidade como um serviço poderia resolvê-la."
    },
    {
      step: 4,
      icon: "lightbulb",
      title: "Passo 4: Proposta de Solução",
      description: "O grupo desenvolve uma proposta inicial de serviço/produto que resolva a dor escolhida, considerando viabilidade e inovação."
    }
  ],
  painExamples: [
    { title: "Wi-Fi instável", description: "Conexão caindo constantemente em ambientes corporativos ou residenciais", icon: "wifi_off" },
    { title: "Falta de segurança", description: "Redes vulneráveis a ataques, invasões e vazamento de dados", icon: "security_update_warning" },
    { title: "Suporte lento", description: "Demora excessiva para resolução de problemas técnicos", icon: "support_agent" }
  ],
  resources: [
    {
      title: "Sebrae - Tendências em Tecnologia e Negócios",
      description: "Material explicativo sobre o papel da TIC no empreendedorismo moderno.",
      icon: "language",
      url: "https://sebrae.com.br/sites/PortalSebrae/conteudos/tendencias"
    },
    {
      title: "Sebrae - Portal do Empreendedor",
      description: "Recursos para criação e desenvolvimento de novos negócios.",
      icon: "bar_chart",
      url: "https://www.gov.br/empresas-e-negocios/pt-br/empreendedor"
    },
    {
      title: "Mercado de Redes e Conectividade no Brasil",
      description: "Análise do cenário atual e oportunidades no setor de TIC.",
      icon: "link",
      url: "https://www.teleco.com.br"
    },
    {
      title: "Design Thinking para Empreendedores",
      description: "Metodologia para identificação de dores e criação de soluções inovadoras.",
      icon: "smartphone",
      url: "https://sebrae.com.br/sites/PortalSebrae/artigos/design-thinking-o-que-e-e-como-aplicar-no-seu-negocio"
    }
  ],
  nextSteps: [
    "Ler o material de apoio sobre tendências em tecnologia e negócios",
    "Refletir sobre as dores identificadas na dinâmica e pesquisar soluções existentes"
  ]
};

export const SPRINTS: CourseSprint[] = [
  {
    id: 1,
    title: "Sprint 1: O 'Hardware'",
    subtitle: "Perfil, Comportamento e Competência Empreendedora",
    hours: 10,
    description: "O Core do Empreendedor: transição de executor de tarefas para identificador de problemas. Mapeamento de competências e topologias no mercado de TI.",
    icon: "memory",
    color: "#0284c7"
  },
  {
    id: 2,
    title: "Sprint 2: A Infraestrutura de Rede",
    subtitle: "Coworking, Startups, Incubadoras e o Novo Ecossistema",
    hours: 10,
    description: "Os agentes de apoio (Incubadoras, Aceleradoras, Investidores) e os novos protocolos de trabalho como Coworking e a Economia Criativa.",
    icon: "hub",
    color: "#0d9488"
  },
  {
    id: 3,
    title: "Sprint 3: O Protocolo Ágil",
    subtitle: "Lean Startup e Produto Mínimo Viável (MVP)",
    hours: 10,
    description: "O ciclo de validação Construir-Medir-Aprender. Entendendo a anatomia do MVP e como resolver o problema desde o Dia 1.",
    icon: "published_with_changes",
    color: "#d97706"
  },
  {
    id: 4,
    title: "Sprint 4: O 'Deploy' do Modelo",
    subtitle: "Business Model Canvas e Plano de Negócios",
    hours: 10,
    description: "Modelagem visual em 1 página com o Business Model Canvas e a estruturação completa para escala no Plano de Negócios.",
    icon: "account_tree",
    color: "#7c3aed"
  }
];

export const SPRINT_1_CONTENT = {
  title: "Sprint 1: O 'Hardware' (10h)",
  subtitle: "Perfil, Comportamento e Competência Empreendedora",
  coreElements: [
    {
      title: "Empreendedorismo e o Empreendedor",
      text: "Não é um dom místico. É um conjunto de práticas focadas em identificar oportunidades tecnológicas e criar soluções sustentáveis."
    },
    {
      title: "Perfil e Comportamento",
      text: "A transição de um executor de tarefas para um identificador de problemas. Envolve assumir riscos calculados e proatividade sistêmica."
    },
    {
      title: "Competência Empreendedora",
      text: "A capacidade de mobilizar recursos (técnicos, financeiros e humanos) para tirar uma ideia do papel e colocá-la no ar."
    }
  ],
  radarAxes: [
    { key: "visao", label: "Visão de Mercado", description: "Capacidade de antecipar necessidades e tendências em redes." },
    { key: "lideranca", label: "Liderança Técnica", description: "Domínio arquitetural para direcionar equipes de TIC." },
    { key: "resiliencia", label: "Resiliência", description: "Capacidade de superar falhas, downtimes e incertezas." },
    { key: "inovacao", label: "Inovação", description: "Criação de valor novo combinando tecnologias de rede." },
    { key: "execucao", label: "Execução", description: "Foco em entregar valor rápido e funcional sem desperdício." }
  ],
  topologies: [
    {
      title: "Intraempreendedorismo",
      icon: "domain",
      tag: "Inovação Interna",
      description: "Inovação dentro de estruturas existentes. O profissional de TI que cria novos processos, otimiza redes e gera novas fontes de receita para a empresa onde já trabalha."
    },
    {
      title: "Empreendedorismo de Mercado",
      icon: "rocket_launch",
      tag: "Startups & Escala",
      description: "Foco na criação de novos negócios, produtos e serviços em TIC. O objetivo é a escala, a captura de valor econômico e a disrupção de mercados tradicionais."
    },
    {
      title: "Empreendedorismo Social",
      icon: "public",
      tag: "Impacto & Sustentabilidade",
      description: "Utilização da engenharia de sistemas e tecnologia para resolver problemas sociais crônicos. O foco principal é o impacto sustentável, com o lucro atuando como combustível, não como fim."
    }
  ]
};

export const SPRINT_2_CONTENT = {
  title: "Sprint 2: A Infraestrutura de Rede (10h)",
  subtitle: "Coworking, Startups, Incubadoras e o Novo Ecossistema",
  comparisonTable: [
    { variable: "Foco Inicial", pme: "Executar um plano de negócios rígido.", startup: "Testar e encontrar um modelo de negócios sustentável." },
    { variable: "Ambiente", pme: "Mercado conhecido, risco calculado.", startup: "Extrema incerteza, alto risco tecnológico." },
    { variable: "Crescimento", pme: "Linear (depende de horas/homem).", startup: "Exponencial (escalável via software e infraestrutura)." },
    { variable: "Estrutura", pme: "Hierárquica e departamentalizada.", startup: "Horizontal, ágil e multifuncional." }
  ],
  ecosystemAgents: [
    {
      phase: "Fase de Concepção",
      agent: "Incubadoras",
      icon: "science",
      description: "Apoio estrutural e gerencial para validar ideias em estágio inicial e transformar projetos de laboratório em empresas reais."
    },
    {
      phase: "Fase de Tração",
      agent: "Aceleradoras",
      icon: "speed",
      description: "Programas intensos e curtos (bootcamps de negócios) que injetam mentoria e recursos para empresas ganharem velocidade de mercado rápido."
    },
    {
      phase: "Fase de Escala",
      agent: "Investidores",
      icon: "payments",
      description: "Capital de Risco (Venture Capital) e Investidores Anjo. Fornecem o combustível financeiro para dominar o mercado globalmente."
    }
  ],
  workProtocols: [
    {
      title: "Coworking",
      icon: "lan",
      description: "Muito mais que aluguel de mesas. São roteadores físicos de inovação. Ambientes de trabalho compartilhados que promovem densidade de talentos, networking orgánico e redução de custos operacionais (OpEx) para novos negócios em TIC."
    },
    {
      title: "Economia Criativa",
      icon: "lightbulb",
      description: "O valor não está mais apenas no hardware ou no cabo de rede, mas na inteligência, no design e na solução inovadora que trafega por eles. A transição de ativos físicos para a monetização de ideias e propriedade intelectual."
    }
  ]
};

export const SPRINT_3_CONTENT = {
  title: "Sprint 3: O Protocolo Ágil (10h)",
  subtitle: "Lean Startup e Produto Mínimo Viável (MVP)",
  loopSteps: [
    {
      step: "CONSTRUIR",
      icon: "code",
      color: "#0284c7",
      description: "Transformar ideias em código/solução o mais rápido possível."
    },
    {
      step: "MEDIR",
      icon: "analytics",
      color: "#0d9488",
      description: "Colocar na rede e coletar dados reais de uso dos clientes."
    },
    {
      step: "APRENDER",
      icon: "psychology",
      color: "#d97706",
      description: "Analisar os dados para decidir: Pivotar (mudar a rota) ou Perseverar (manter a estratégia)."
    }
  ],
  loopObjective: "Minimizar o desperdício de tempo e recursos construindo produtos que ninguém quer usar. A velocidade de iteração é a sua maior vantagem competitiva.",
  mvpComparison: {
    error: {
      title: "Erro: Construir por Partes",
      subtitle: "Pneu → Chassis → Carcaça → Carro",
      text: "Construir por partes não entrega valor imediato. O cliente fica frustrado até o 'deploy' final e você não colhe feedback no caminho."
    },
    correct: {
      title: "Acerto: Evolução Funcional Incremental",
      subtitle: "Skate → Patinete → Bicicleta → Moto → Carro",
      text: "Cada iteração é um produto completo e funcional que resolve o problema básico de locomoção desde o Dia 1. O sistema evolui com o feedback real do usuário."
    }
  }
};

export const SPRINT_4_CONTENT = {
  title: "Sprint 4: O 'Deploy' do Modelo (10h)",
  subtitle: "Business Model Canvas e Plano de Negócios",
  canvasArchitectureIntro: "A Arquitetura do Seu Negócio (Em 1 Página) - Business Model Generation. Uma ferramenta visual para desenhar, testar e pivotar modelos de negócios antes de escrever páginas de planejamento.",
  canvasDefaultBlocks: [
    {
      id: "partnerships",
      title: "Parcerias Principais",
      subtitle: "Lado Esquerdo (Infra/Operação)",
      category: "infra",
      icon: "handshake",
      placeholder: "Ex: Provedores de nuvem, fabricantes de hardware de rede, ISPs locais",
      content: ""
    },
    {
      id: "activities",
      title: "Atividades-Chave",
      subtitle: "Lado Esquerdo (Infra/Operação)",
      category: "infra",
      icon: "build",
      placeholder: "Ex: Desenvolvimento de software, monitoramento NOC 24/7, otimização de rotas",
      content: ""
    },
    {
      id: "resources",
      title: "Recursos-Chave",
      subtitle: "Lado Esquerdo (Infra/Operação)",
      category: "infra",
      icon: "inventory_2",
      placeholder: "Ex: Servidores em nuvem, engenheiros de redes, patentes e algoritmo proprietário",
      content: ""
    },
    {
      id: "value_prop",
      title: "Proposta de Valor",
      subtitle: "Centro (O Core)",
      category: "core",
      icon: "memory",
      placeholder: "Qual problema você resolve? Ex: Conectividade ultra-estável sem downtime para empresas",
      content: ""
    },
    {
      id: "relationships",
      title: "Relacionamento com Clientes",
      subtitle: "Lado Direito (Rede/Mercado)",
      category: "market",
      icon: "favorite",
      placeholder: "Ex: Suporte técnico automatizado com IA e gerente de conta dedicado",
      content: ""
    },
    {
      id: "channels",
      title: "Canais de Distribuição",
      subtitle: "Lado Direito (Rede/Mercado)",
      category: "market",
      icon: "router",
      placeholder: "Ex: Marketplace online, vendas B2B diretas, parceiros de integração",
      content: ""
    },
    {
      id: "segments",
      title: "Segmentos de Clientes",
      subtitle: "Lado Direito (Rede/Mercado)",
      category: "market",
      icon: "groups",
      placeholder: "Ex: Startups de tecnologia, provedores de internet regional, indústrias 4.0",
      content: ""
    },
    {
      id: "cost_structure",
      title: "Estrutura de Custos",
      subtitle: "Base (O Servidor/Finanças)",
      category: "finance",
      icon: "receipt_long",
      placeholder: "Ex: Licenciamento de software, infraestrutura AWS/GCP, folha técnica",
      content: ""
    },
    {
      id: "revenue_streams",
      title: "Fontes de Receita",
      subtitle: "Base (O Servidor/Finanças)",
      category: "finance",
      icon: "attach_money",
      placeholder: "Ex: Assinatura mensal SaaS, suporte premium por hora, comissão por tráfego",
      content: ""
    }
  ] as CanvasBlock[],
  scalingProcess: {
    canvas: {
      title: "Modelagem (O Canvas)",
      focus: "Descoberta e Validação",
      characteristics: "Ágil, visual, iterativo, baseado em hipóteses.",
      usage: "Entender se o negócio faz sentido no mundo real."
    },
    businessPlan: {
      title: "Planejamento (O Plano de Negócios)",
      focus: "Execução e Escala",
      characteristics: "Documento detalhado, projeções financeiras, plano de marketing operacional.",
      usage: "Conseguir crédito, captar grandes investimentos e guiar a expansão da infraestrutura."
    }
  }
};

export const TI_STACK_CONTENT = {
  title: "O 'Stack' do Empreendedor de TI",
  subtitle: "A Arquitetura Completa da Disciplina (Síntese)",
  layers: [
    {
      layer: 4,
      name: "Camada 4 (Aplicação)",
      concept: "Plano de Negócios",
      description: "A interface final, estruturada e detalhada para investidores e escala operacional.",
      icon: "layers",
      color: "#7c3aed"
    },
    {
      layer: 3,
      name: "Camada 3 (Apresentação)",
      concept: "Canvas BMG",
      description: "O modelo visual que formata e traduz a lógica de como o sistema fará dinheiro.",
      icon: "view_quilt",
      color: "#d97706"
    },
    {
      layer: 2,
      name: "Camada 2 (Transporte/Rede)",
      concept: "Lean & MVP",
      description: "A validação. O protocolo de tráfego de dados reais entre o seu produto e o mercado.",
      icon: "swap_calls",
      color: "#0d9488"
    },
    {
      layer: 1,
      name: "Camada 1 (Física)",
      concept: "O Empreendedor e o Ecossistema",
      description: "A infraestrutura base. Sem perfil de resolução de problemas e conexões, o sistema não liga.",
      icon: "developer_board",
      color: "#0284c7"
    }
  ]
};

export const REFERENCES_CONTENT = {
  title: "Suas Referências de Base (O Kit de Ferramentas)",
  hardKnowledge: [
    {
      author: "Gauthier (2010) e Sertek (2007)",
      title: "Empreendedorismo",
      type: "Base Teórica (Hard Knowledge)",
      icon: "menu_book"
    },
    {
      author: "Carvalho (2009)",
      title: "Empreendedorismo: como planejar e administrar seu negócio",
      type: "Base Teórica (Hard Knowledge)",
      icon: "menu_book"
    },
    {
      author: "Bernardi (2003)",
      title: "Manual de empreendedorismo e gestão",
      type: "Base Teórica (Hard Knowledge)",
      icon: "menu_book"
    }
  ],
  softExecution: [
    {
      author: "Dolabela (2008)",
      title: "O Segredo de Luísa",
      subtitle: "Como uma ideia vira plano.",
      type: "Inspiração e Casos Reais (Soft & Execution)",
      icon: "auto_stories"
    },
    {
      author: "Mezrich (2010)",
      title: "Bilionários Por Acaso",
      subtitle: "A criação do Facebook: o ápice do empreendedorismo em redes.",
      type: "Inspiração e Casos Reais (Soft & Execution)",
      icon: "auto_stories"
    }
  ]
};

export interface AulaItem {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  description: string;
  status: 'available' | 'coming_soon' | 'scheduled';
  duration: string;
  badge: string;
  tags: string[];
  icon: string;
  routeId?: string;
  sprintAssociated?: string;
  topics?: string[];
}

export const AULAS_LIST: AulaItem[] = [
  {
    id: 'aula1',
    number: 1,
    title: 'Aula 1: Dinâmica das Dores em Redes',
    subtitle: 'Identificação e Priorização de Problemas Reais do Mercado de Redes/TIC',
    description: 'Atividade prática em grupo focada em levantar dores de conectividade, infraestrutura e suporte, aplicando a matriz de impacto x viabilidade para definir a oportunidade de negócio.',
    status: 'available',
    duration: '60 min',
    badge: '🛠️ Atividade Prática / Dinâmica',
    tags: ['Dores em Redes', 'Brainstorming', 'Problema & Oportunidade'],
    icon: 'groups',
    routeId: 'aula1',
    sprintAssociated: 'Sprint 1: O Hardware (Perfil & Problema)',
    topics: [
      'Divisão dos Grupos de Trabalho (4 a 5 integrantes)',
      'Levantamento de 3 Dores Críticas em Redes/TIC',
      'Matriz de Priorização (Esforço x Impacto)',
      'Definição do Problema Central e Cadastro no Painel'
    ]
  },
  {
    id: 'aula2',
    number: 2,
    title: 'Aula 2: A Jornada do Empreendedor em TI',
    subtitle: 'Como o conhecimento técnico resolve problemas reais de mercado.',
    description: 'Estudo de caso da Logística Expressa & Cia, transição da visão reativa para empreendedora, arquitetura tolerante a falhas (MikroTik Link Duplo 4G/Fibra), cabeamento estruturado e desafio de campo.',
    status: 'available',
    duration: '80 min',
    badge: '📊 Apresentação & Desafio Prático',
    tags: ['Estudo de Caso', 'Tolerância a Falhas', 'Failover', 'Cabeamento'],
    icon: 'trending_up',
    routeId: 'aula2',
    sprintAssociated: 'Sprint 1: O Hardware (Perfil & Problema)',
    topics: [
      'O Dilema do Sr. Roberto & Diagnóstico de Negócio',
      'Visão Reativa vs Visão Empreendedora',
      'Solução Técnica: Failover MikroTik (Fibra + 4G/5G)',
      'Desafio de Campo: Folha de Atividades Módulos A, B, C e D'
    ]
  },
  {
    id: 'aula3',
    number: 3,
    title: 'Aula 3: As 10 CCEs e o Perfil Empreendedor em Redes',
    subtitle: 'Dominando as 10 Características do Comportamento Empreendedor.',
    description: 'Análise detalhada das 10 CCEs (metodologia McClelland/Sebrae/ONU), modelo de negócios MSP de receita recorrente (R$650/mês), teste de autoavaliação com pontuação dinâmica (50 pts) e RPG Empreendedor da Brisanet.',
    status: 'available',
    duration: '90 min',
    badge: '🧠 Diagnóstico CCEs & RPG Real',
    tags: ['CCEs Sebrae/ONU', 'Soft Skills', 'Modelo MSP', 'RPG Brisanet'],
    icon: 'psychology',
    routeId: 'aula3',
    sprintAssociated: 'Sprint 1: O Hardware (Perfil & Problema)',
    topics: [
      'Hard Skills vs Soft Skills no Mercado de TIC',
      'As 10 CCEs e os 3 Conjuntos (Realização, Planejamento, Poder)',
      'Transição do Técnico Apaga-Fogo para o Modelo MSP Recorrente',
      'Teste Oficial de Autoavaliação CCE (Pontuação /50)',
      'RPG Empreendedor: Os 3 Dilemas Históricos da Brisanet'
    ]
  },
  {
    id: 'aula4',
    number: 4,
    title: 'Aula 4: Tipos de Empreendedorismo e Posicionamento em Redes',
    subtitle: 'Classificação dos modelos de empreendedorismo e posicionamento estratégico em TIC.',
    description: 'Panorama Serasa Experian, Empreendedorismo por Oportunidade vs Necessidade, Intraempreendedorismo (Caso Red Hat), a armadilha do técnico Faz-Tudo vs Especialista e declaração de posicionamento comercial.',
    status: 'available',
    duration: '90 min',
    badge: '🎯 Posicionamento & Modelos TIC',
    tags: ['Tipos de Empreendedorismo', 'Serasa Experian', 'Intraempreendedorismo', 'Red Hat', 'Posicionamento'],
    icon: 'storefront',
    routeId: 'aula4',
    sprintAssociated: 'Sprint 1: O Hardware (Perfil & Problema)',
    topics: [
      'Mitos e Verdades sobre Empreender (Panorama Serasa Experian)',
      'Empreendedorismo por Oportunidade vs Necessidade',
      'Os 6 Modelos de Empreendedorismo em TIC',
      'Intraempreendedorismo & Cultura Open Organization (Red Hat)',
      'A Armadilha do Técnico Faz-Tudo vs O Especialista Posicionado',
      'Declaração de Posicionamento Profissional em 3 Passos'
    ]
  },
  {
    id: 'aula5',
    number: 5,
    title: 'Aula Especial: "Fome de Poder" (The Founder)',
    subtitle: 'O Modelo McDonald\'s Aplicado à Infraestrutura, Processos e Redes.',
    description: 'Estudo de caso prático do filme The Founder: engenharia de processos na quadra de tênis, paralelo com cabeamento estruturado e Racks, diagnóstico das 10 CCEs (Irmãos McDonald vs Ray Kroc), corte radical do cardápio e os riscos da informalidade.',
    status: 'available',
    duration: '90 min',
    badge: '🎬 Estudo de Caso Cinematográfico',
    tags: ['The Founder', 'Engenharia de Processos', 'CCEs', 'Posicionamento', 'Infraestrutura'],
    icon: 'movie',
    routeId: 'aula5',
    sprintAssociated: 'Sprint 1: O Hardware (Perfil & Problema)',
    topics: [
      'Engenharia de Processos & Infraestrutura Física (Paralelo com Racks e Cabeamento)',
      'Diagnóstico de Dores de Mercado (Drive-in Tradicional vs Speedee System)',
      'Diagnóstico das 10 CCEs: Irmãos McDonald vs Ray Kroc',
      'Posicionamento Estratégico: O Corte Radical do Cardápio (27 para 3 itens)',
      'Intraempreendedorismo na Prática (Fred Turner & Harry Sonneborn)',
      'O Acordo de Cavalheiros e os Riscos da Informalidade em Contratos de TI'
    ]
  }
];

